import { z } from 'zod';

/**
 * File Manager tenant (`/api/files/*`, `/api/google-drive/*`, file dei gruppi
 * `/api/messenger/workgroups/:id/files*`). Source of truth:
 * `packages/backend/src/routes/storage/file-manager.routes.ts`,
 * `.../storage/google-drive.routes.ts`, `.../messaging/messenger.routes.ts`.
 *
 * Queste rotte rispondono con il payload TOP-LEVEL (`{ success, files }`,
 * `{ success, quota }`, ...) invece dell'envelope `{ success, data }`: i tipi
 * `*Response` qui sotto descrivono la shape reale letta dal FE via
 * `apiClient.getTopLevel<T>()`.
 */

const isoOrNull = z.string().nullable();

export const fileManagerFileSchema = z
  .object({
    id: z.string(),
    file_name: z.string(),
    file_size: z.number(),
    mime_type: z.string(),
    category: z.string(),
    parent_folder_id: z.string().nullable(),
    cloud_synced: z.boolean(),
    cloud_synced_at: isoOrNull,
    created_at: z.string(),
    updated_at: z.string(),
    uploaded_by_name: z.string().optional(),
    is_deleted: z.boolean().optional(),
    ai_summary: z.string().nullable().optional(),
    ai_summary_at: z.string().nullable().optional(),
    shared_by_name: z.string().optional(),
    permission: z.string().optional(),
  })
  .passthrough();
export type FileManagerFile = z.infer<typeof fileManagerFileSchema>;

export const fileManagerFolderSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    parent_id: z.string().nullable(),
    path: z.string(),
    is_system: z.boolean(),
    visibility: z.string(),
    created_at: z.string(),
    permission: z.string().optional(),
    shared_by_name: z.string().optional(),
  })
  .passthrough();
export type FileManagerFolder = z.infer<typeof fileManagerFolderSchema>;

export interface FileManagerFolderTreeNode {
  id: string;
  name: string;
  path: string;
  is_system: boolean;
  children: FileManagerFolderTreeNode[];
}
export const fileManagerFolderTreeNodeSchema: z.ZodType<FileManagerFolderTreeNode> = z.lazy(() =>
  z.object({
    id: z.string(),
    name: z.string(),
    path: z.string(),
    is_system: z.boolean(),
    children: z.array(fileManagerFolderTreeNodeSchema),
  }),
);

export const fileManagerQuotaSchema = z
  .object({
    max_bytes: z.number(),
    used_bytes: z.number(),
    recordings_enabled: z.boolean(),
    recordings_max_bytes: z.number(),
    recordings_used_bytes: z.number(),
    videos_used_bytes: z.number(),
    documents_used_bytes: z.number(),
    recordings_retention_days: z.number().optional(),
    alert_threshold_pct: z.number().optional(),
    ai_summary_enabled: z.boolean().optional(),
  })
  .passthrough();
export type FileManagerQuota = z.infer<typeof fileManagerQuotaSchema>;

export const fileManagerStorageSettingsSchema = z
  .object({
    recordings_enabled: z.boolean().optional(),
    recordings_max_bytes: z.number().optional(),
    recordings_retention_days: z.number().optional(),
    alert_threshold_pct: z.number().optional(),
    ai_summary_enabled: z.boolean().optional(),
  })
  .passthrough();
export type FileManagerStorageSettings = z.infer<typeof fileManagerStorageSettingsSchema>;

export const fileManagerAnalyticsSchema = z.object({
  categoryBreakdown: z.array(z.object({ category: z.string(), file_count: z.string(), total_bytes: z.string() })),
  topFiles: z.array(
    z.object({ id: z.string(), file_name: z.string(), file_size: z.number(), category: z.string(), created_at: z.string() }),
  ),
  monthlyTrend: z.array(z.object({ month: z.string(), file_count: z.string(), total_bytes: z.string() })),
});
export type FileManagerAnalytics = z.infer<typeof fileManagerAnalyticsSchema>;

export const googleDriveFileSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    mimeType: z.string(),
    size: z.string().optional(),
    modifiedTime: z.string().optional(),
    createdTime: z.string().optional(),
    iconLink: z.string().optional(),
    webViewLink: z.string().optional(),
    isFolder: z.boolean(),
  })
  .passthrough();
export type GoogleDriveFile = z.infer<typeof googleDriveFileSchema>;

export const googleDriveAccountSchema = z
  .object({
    id: z.string(),
    account_email: z.string().nullable(),
    account_label: z.string().nullable(),
    enabled: z.boolean(),
    config: z.object({ enabled_scopes: z.array(z.string()).optional() }).passthrough().optional(),
  })
  .passthrough();
export type GoogleDriveAccount = z.infer<typeof googleDriveAccountSchema>;

/** File di un gruppo di lavoro (`messenger_workgroup_files`, tabella dedicata). */
export const workgroupFileSchema = z
  .object({
    id: z.string(),
    file_name: z.string(),
    cloud_path: z.string().nullable(),
    file_size: z.number().nullable().optional(),
    mime_type: z.string().nullable().optional(),
    created_at: z.string().nullable().optional(),
    source: z.string().nullable().optional(),
    ai_summary: z.string().nullable().optional(),
    ai_summary_at: z.string().nullable().optional(),
  })
  .passthrough();
export type WorkgroupFile = z.infer<typeof workgroupFileSchema>;

/** Gruppo di lavoro come restituito da `GET /messenger/workgroups` (campi letti dal File Manager). */
export const workgroupSummarySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    member_count: z.number().optional(),
    members: z.array(z.unknown()).optional(),
  })
  .passthrough();
export type WorkgroupSummary = z.infer<typeof workgroupSummarySchema>;

// ───── Risposte top-level ──────────────────────────────────────────────────

const ok = { success: z.literal(true) };

export const fileManagerFilesResponseSchema = z.object({ ...ok, files: z.array(fileManagerFileSchema) });
export type FileManagerFilesResponse = z.infer<typeof fileManagerFilesResponseSchema>;

export const fileManagerFoldersResponseSchema = z.object({ ...ok, folders: z.array(fileManagerFolderSchema) });
export type FileManagerFoldersResponse = z.infer<typeof fileManagerFoldersResponseSchema>;

export const fileManagerFolderTreeResponseSchema = z.object({ ...ok, tree: z.array(fileManagerFolderTreeNodeSchema) });
export type FileManagerFolderTreeResponse = z.infer<typeof fileManagerFolderTreeResponseSchema>;

export const fileManagerSharedResponseSchema = z.object({
  ...ok,
  files: z.array(fileManagerFileSchema),
  folders: z.array(fileManagerFolderSchema),
});
export type FileManagerSharedResponse = z.infer<typeof fileManagerSharedResponseSchema>;

export const fileManagerQuotaResponseSchema = z.object({ ...ok, quota: fileManagerQuotaSchema });
export type FileManagerQuotaResponse = z.infer<typeof fileManagerQuotaResponseSchema>;

export const fileManagerSettingsResponseSchema = z.object({ ...ok, settings: fileManagerStorageSettingsSchema });
export type FileManagerSettingsResponse = z.infer<typeof fileManagerSettingsResponseSchema>;

export const fileManagerAnalyticsResponseSchema = z.object({ ...ok, analytics: fileManagerAnalyticsSchema });
export type FileManagerAnalyticsResponse = z.infer<typeof fileManagerAnalyticsResponseSchema>;

export const fileManagerDownloadResponseSchema = z.object({
  ...ok,
  downloadUrl: z.string().optional(),
  fileName: z.string().optional(),
});
export type FileManagerDownloadResponse = z.infer<typeof fileManagerDownloadResponseSchema>;

export const fileManagerAiSummaryResponseSchema = z.object({
  ...ok,
  summary: z.string().nullable(),
  generatedAt: z.string().nullable(),
});
export type FileManagerAiSummaryResponse = z.infer<typeof fileManagerAiSummaryResponseSchema>;

export const fileManagerOkResponseSchema = z.object({ ...ok }).passthrough();
export type FileManagerOkResponse = z.infer<typeof fileManagerOkResponseSchema>;

export const googleDriveAccountsResponseSchema = z.object({ ...ok, accounts: z.array(googleDriveAccountSchema) });
export type GoogleDriveAccountsResponse = z.infer<typeof googleDriveAccountsResponseSchema>;

export const googleDriveFilesResponseSchema = z.object({
  ...ok,
  files: z.array(googleDriveFileSchema),
  nextPageToken: z.string().nullable().optional(),
});
export type GoogleDriveFilesResponse = z.infer<typeof googleDriveFilesResponseSchema>;

export const googleDriveFoldersResponseSchema = z.object({ ...ok, folders: z.array(googleDriveFileSchema) });
export type GoogleDriveFoldersResponse = z.infer<typeof googleDriveFoldersResponseSchema>;

/** `GET /integrations/google/oauth/start` → `data: { authUrl }`. */
export const googleOauthStartDataSchema = z.object({ authUrl: z.string() });
export type GoogleOauthStartData = z.infer<typeof googleOauthStartDataSchema>;

/** `GET /company-wiki/sources/by-file/:id` → `data`: impatto wiki di un file. */
export const wikiFileImpactSchema = z.object({
  count: z.number(),
  sources: z.array(z.object({ id: z.string(), title: z.string(), kind: z.string() })),
});
export type WikiFileImpact = z.infer<typeof wikiFileImpactSchema>;
